import React, { useEffect, useState, useRef } from 'react';

import { createPortal } from 'react-dom';

import { formatProductTitle } from './formatProductTitle';

import { resolveMediaUrl } from './productMediaUtils';

import { useFullscreenOverlay } from './useFullscreenOverlay';



/**

 * Portaled fullscreen player (same strategy as ImageModal).

 * React-Bootstrap Modal + overflow on ancestors often breaks <video> (black screen, no controls, iOS quirks).

 */

const VideoModal = ({

  show,

  video,

  alt,

  onClose,

  noSound = false,

  onNext,

  onPrev,

  hasMultipleImages,

  currentIndex,

  totalImages,

}) => {

  const safeSrc = resolveMediaUrl(video);

  const [loadError, setLoadError] = useState(false);

  const backdropRef = useFullscreenOverlay(show);

  const videoRef = useRef(null);



  useEffect(() => {

    setLoadError(false);

  }, [safeSrc, show]);



  useEffect(() => {

    if (!show) return;

    const backdrop = backdropRef.current;

    if (!backdrop) return;



    const blockSafariGesture = (e) => e.preventDefault();

    const opts = { passive: false };

    backdrop.addEventListener('gesturestart', blockSafariGesture, opts);

    backdrop.addEventListener('gesturechange', blockSafariGesture, opts);

    backdrop.addEventListener('gestureend', blockSafariGesture, opts);



    return () => {

      backdrop.removeEventListener('gesturestart', blockSafariGesture);

      backdrop.removeEventListener('gesturechange', blockSafariGesture);

      backdrop.removeEventListener('gestureend', blockSafariGesture);

    };

  }, [show, backdropRef]);



  useEffect(() => {

    if (!show || !safeSrc) return;

    const v = videoRef.current;

    if (!v) return;

    v.muted = true;

    v.volume = noSound ? 0 : 1;

    v.load();

    const p = v.play();

    if (p !== undefined) p.catch(() => {});

  }, [show, safeSrc, noSound]);



  useEffect(() => {

    if (!show) return;



    const handleKeyDown = (e) => {

      if (e.key === 'ArrowRight' && hasMultipleImages) {

        onNext?.();

      } else if (e.key === 'ArrowLeft' && hasMultipleImages) {

        onPrev?.();

      } else if (e.key === 'Escape') {

        onClose();

      }

    };



    document.addEventListener('keydown', handleKeyDown);

    return () => {

      document.removeEventListener('keydown', handleKeyDown);

    };

  }, [show, hasMultipleImages, onNext, onPrev, onClose]);



  const handleBackdropClick = (e) => {

    if (e.target.classList.contains('image-modal-backdrop')) {

      onClose();

    }

  };



  const displayTitle = formatProductTitle(alt || '');



  if (!show) return null;



  return createPortal(

    <div

      ref={backdropRef}

      className="image-modal-backdrop video-modal-portal"

      role="dialog"

      aria-modal="true"

      aria-label={displayTitle || 'Product video'}

      onClick={handleBackdropClick}

    >

      <div className="video-modal-portal-shell" onClick={(e) => e.stopPropagation()}>

        <div className="image-modal-toolbar">

          {hasMultipleImages && (

            <>

              <button

                type="button"

                className="image-modal-btn image-modal-btn--side image-modal-btn--prev"

                onClick={onPrev}

                aria-label="Previous"

              >

                ❮

              </button>

              <button

                type="button"

                className="image-modal-btn image-modal-btn--side image-modal-btn--next"

                onClick={onNext}

                aria-label="Next"

              >

                ❯

              </button>

            </>

          )}

          <span className="text-white small me-auto opacity-75 px-2 d-none d-sm-inline">

            {hasMultipleImages && totalImages > 0

              ? `${(currentIndex ?? 0) + 1} / ${totalImages}`

              : ''}

          </span>

          <button

            type="button"

            className="image-modal-btn image-modal-btn--close"

            onClick={onClose}

            aria-label="Close"

          >

            ×

          </button>

        </div>



        <div className="video-modal-portal-stage">

          {safeSrc && !loadError && (

            <video

              ref={videoRef}

              key={safeSrc}

              src={safeSrc}

              controls

              playsInline

              muted

              loop

              autoPlay

              preload="auto"

              data-no-sound={noSound}

              className="video-modal-portal-video"

              onLoadedData={(e) => {

                if (noSound) {

                  e.target.volume = 0;

                  e.target.muted = true;

                }

              }}

              onCanPlay={(e) => {

                if (noSound) {

                  e.target.volume = 0;

                  e.target.muted = true;

                }

                e.target.play().catch(() => {});

              }}

              onVolumeChange={(e) => {

                if (!noSound) return;

                if (e.target.volume > 0) {

                  e.target.volume = 0;

                  e.target.muted = true;

                }

              }}

              onError={() => {

                console.warn('Video failed to load:', safeSrc);

                setLoadError(true);

              }}

            >

              Your browser does not support video.

            </video>

          )}



          {safeSrc && loadError && (

            <div className="text-white px-3 py-4 text-center" style={{ maxWidth: 'min(100%, 420px)' }}>

              <p className="mb-2 fw-semibold">Could not play this video.</p>

              <p className="small mb-3" style={{ opacity: 0.85 }}>

                Open the link below. If you see your site home page instead of a download, the server is routing video

                URLs to the React app—upload the file under <code className="text-white-50">public/</code> or fix

                hosting (Netlify/SPA) so <code className="text-white-50">.mp4</code> files are served as static files.

              </p>

              <a

                href={safeSrc}

                target="_blank"

                rel="noopener noreferrer"

                className="btn btn-sm btn-light mb-2"

              >

                Open video URL in new tab

              </a>

              <p className="small text-break mb-0 mt-2" style={{ opacity: 0.55 }}>

                {safeSrc}

              </p>

            </div>

          )}

        </div>



        {displayTitle ? <p className="image-modal-caption text-white">{displayTitle}</p> : null}

      </div>

    </div>,

    document.body

  );

};



export default VideoModal;

